import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import LucideIcon from './LucideIcon';
import WhiskeyLogo from './layout/WhiskeyLogo';
import { useAuth } from '../contexts/AuthContext';

type AuthView = 'login' | 'signup' | 'migrate-password';

interface UsernameBasedAuthProps {
  initialView?: 'login' | 'signup';
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'var(--surface-sunken)',
  color: 'var(--text-primary)',
  fontSize: 14,
  outline: 'none',
  transition: 'border-color 140ms ease-out, box-shadow 140ms ease-out',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 12.5,
  fontWeight: 600,
  color: 'var(--text-secondary)',
  marginBottom: 6,
};

function handleFocus(e: React.FocusEvent<HTMLInputElement>) {
  e.target.style.borderColor = 'var(--ember)';
  e.target.style.boxShadow = '0 0 0 3px var(--ember-soft)';
}
function handleBlur(e: React.FocusEvent<HTMLInputElement>) {
  e.target.style.borderColor = 'var(--border)';
  e.target.style.boxShadow = 'none';
}

const UsernameBasedAuth: React.FC<UsernameBasedAuthProps> = ({ initialView = 'login' }) => {
  const navigate = useNavigate();
  const { signIn, signUp, completePasswordMigration } = useAuth();

  const [view, setView] = useState<AuthView>(initialView);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: ''
  });
  const [migrationData, setMigrationData] = useState<{ username: string; firstName: string; lastName: string } | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const goTo = (next: 'login' | 'signup') => {
    setView(next);
    setError('');
    navigate(`/${next}`);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await signIn(formData.username, formData.password);
      if (result?.requiresPasswordReset) {
        setMigrationData({ username: result.username, firstName: result.firstName, lastName: result.lastName });
        setView('migrate-password');
        setFormData({ ...formData, password: '', confirmPassword: '' });
      }
      // A successful (non-migration) login updates AuthContext's user state,
      // which flips AppContent's isAuthenticated branch and renders the
      // authenticated app -- no manual navigation needed here.
    } catch (err: any) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await signUp({
        username: formData.username,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName
      });
    } catch (err: any) {
      setError(err.message || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleMigratePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (!migrationData) {
      setError('Migration data not found');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await completePasswordMigration(migrationData.username, formData.password, formData.confirmPassword);
    } catch (err: any) {
      setError(err.message || 'Password migration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ws-shell" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px 20px', background: 'var(--paper)' }}>
      <div className="ws-fade-up" style={{ width: '100%', maxWidth: 400, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, padding: 36, boxShadow: '0 1px 2px oklch(0.2 0.02 260 / 0.06), 0 12px 32px oklch(0.2 0.02 260 / 0.05)' }}>
        <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, textDecoration: 'none', marginBottom: 28 }}>
          <WhiskeyLogo size={26} />
          <span className="ws-display" style={{ fontWeight: 700, fontSize: 16, color: 'var(--text-primary)' }}>Whiskey Sensei</span>
        </Link>

        <h1 className="ws-display" style={{ margin: '0 0 4px', fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>
          {view === 'login' && 'Welcome back'}
          {view === 'signup' && 'Create your account'}
          {view === 'migrate-password' && `Welcome back, ${migrationData?.firstName || ''}`}
        </h1>
        <p style={{ margin: '0 0 24px', fontSize: 13.5, color: 'var(--text-tertiary)' }}>
          {view === 'login' && 'Log in to pick up where you left off.'}
          {view === 'signup' && 'Free to start -- takes about a minute.'}
          {view === 'migrate-password' && 'We upgraded our login system -- please set a new password.'}
        </p>

        {error && (
          <div style={{ marginBottom: 18, padding: '10px 14px', borderRadius: 8, background: 'oklch(0.6 0.2 25 / 0.1)', border: '1px solid oklch(0.6 0.2 25 / 0.35)', color: 'var(--danger)', fontSize: 13 }}>
            {error}
          </div>
        )}

        {view === 'migrate-password' && (
          <form onSubmit={handleMigratePassword} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={labelStyle} htmlFor="migrate-new-password">New password</label>
              <input id="migrate-new-password" type="password" required style={inputStyle} onFocus={handleFocus} onBlur={handleBlur}
                value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} />
              <p style={{ margin: '6px 0 0', fontSize: 11.5, color: 'var(--text-tertiary)' }}>8+ characters, uppercase, lowercase, number, symbol</p>
            </div>
            <div>
              <label style={labelStyle} htmlFor="migrate-confirm-password">Confirm password</label>
              <input id="migrate-confirm-password" type="password" required style={inputStyle} onFocus={handleFocus} onBlur={handleBlur}
                value={formData.confirmPassword} onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })} />
            </div>
            <button type="submit" disabled={loading} className="btn-live" style={{ width: '100%', padding: '12px', borderRadius: 10, border: 'none', background: 'var(--ink)', color: 'white', fontWeight: 700, fontSize: 14.5, cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.7 : 1 }}>
              {loading ? 'Setting password…' : 'Set new password'}
            </button>
            <button
              type="button"
              onClick={() => { setView('login'); setMigrationData(null); setFormData({ ...formData, password: '', confirmPassword: '' }); setError(''); }}
              style={{ textAlign: 'center', background: 'none', border: 'none', padding: 0, fontSize: 13, fontWeight: 600, color: 'var(--text-tertiary)', cursor: 'pointer' }}
            >
              Back to login
            </button>
          </form>
        )}

        {view === 'login' && (
          <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={labelStyle} htmlFor="login-username">Username or email</label>
              <input id="login-username" type="text" required style={inputStyle} onFocus={handleFocus} onBlur={handleBlur}
                value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <label style={labelStyle} htmlFor="login-password">Password</label>
                <Link to="/forgot-password" style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ember-strong)', textDecoration: 'none', marginBottom: 6 }}>Forgot?</Link>
              </div>
              <input id="login-password" type="password" required style={inputStyle} onFocus={handleFocus} onBlur={handleBlur}
                value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} placeholder="••••••••" />
            </div>
            <button type="submit" disabled={loading} className="btn-live" style={{ width: '100%', padding: '12px', borderRadius: 10, border: 'none', background: 'var(--ink)', color: 'white', fontWeight: 700, fontSize: 14.5, cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.7 : 1 }}>
              {loading ? 'Signing in…' : 'Log in'}
            </button>

            <button
              type="button"
              disabled={loading}
              onClick={async () => {
                setError('');
                setLoading(true);
                try {
                  await signIn('demo@whiskeysensei.com', 'demopassword');
                } catch (err: any) {
                  setError(err.message || 'Demo login failed');
                } finally {
                  setLoading(false);
                }
              }}
              style={{
                width: '100%',
                padding: '11px',
                borderRadius: 10,
                border: '1px solid var(--border)',
                background: 'var(--surface-sunken)',
                color: 'var(--text-primary)',
                fontWeight: 700,
                fontSize: 13.5,
                cursor: loading ? 'default' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                transition: 'background 140ms ease'
              }}
            >
              <span style={{ color: 'var(--ember)' }}>⚡</span> One-Click Demo Access
            </button>

            <div style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-tertiary)' }}>
              No account?{' '}
              <button type="button" onClick={() => goTo('signup')} style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', fontWeight: 700, color: 'var(--ember-strong)', cursor: 'pointer' }}>
                Start free
              </button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, paddingTop: 14, borderTop: '1px solid var(--border)', fontSize: 11, color: 'var(--text-tertiary)' }}>
              <LucideIcon name="status-locked" size={12} /> Secure authentication
            </div>
          </form>
        )}

        {view === 'signup' && (
          <form onSubmit={handleSignup} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle} htmlFor="signup-first-name">First name</label>
                <input id="signup-first-name" type="text" required style={inputStyle} onFocus={handleFocus} onBlur={handleBlur}
                  value={formData.firstName} onChange={(e) => setFormData({ ...formData, firstName: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle} htmlFor="signup-last-name">Last name</label>
                <input id="signup-last-name" type="text" required style={inputStyle} onFocus={handleFocus} onBlur={handleBlur}
                  value={formData.lastName} onChange={(e) => setFormData({ ...formData, lastName: e.target.value })} />
              </div>
            </div>
            <div>
              <label style={labelStyle} htmlFor="signup-username">Username or email</label>
              <input id="signup-username" type="text" required style={inputStyle} onFocus={handleFocus} onBlur={handleBlur}
                value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} />
            </div>
            <div>
              <label style={labelStyle} htmlFor="signup-password">Password</label>
              <input id="signup-password" type="password" required style={inputStyle} onFocus={handleFocus} onBlur={handleBlur}
                value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} />
              <p style={{ margin: '6px 0 0', fontSize: 11.5, color: 'var(--text-tertiary)' }}>8+ characters, uppercase, lowercase, number, symbol</p>
            </div>
            <div>
              <label style={labelStyle} htmlFor="signup-confirm-password">Confirm password</label>
              <input id="signup-confirm-password" type="password" required style={inputStyle} onFocus={handleFocus} onBlur={handleBlur}
                value={formData.confirmPassword} onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })} />
            </div>
            <button type="submit" disabled={loading} className="btn-live" style={{ width: '100%', padding: '12px', borderRadius: 10, border: 'none', background: 'var(--ink)', color: 'white', fontWeight: 700, fontSize: 14.5, cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.7 : 1 }}>
              {loading ? 'Creating account…' : 'Create account'}
            </button>
            {/* Consent is given by creating the account, so the link has to be
                here, before the button is pressed -- not buried in a footer. */}
            <p style={{ margin: 0, textAlign: 'center', fontSize: 11.5, lineHeight: 1.5, color: 'var(--text-tertiary)' }}>
              By creating an account you agree to our{' '}
              <Link to="/terms" style={{ color: 'var(--ember-strong)', fontWeight: 600, textDecoration: 'none' }}>Terms of Use</Link>
              {' '}and{' '}
              <Link to="/privacy" style={{ color: 'var(--ember-strong)', fontWeight: 600, textDecoration: 'none' }}>Privacy Policy</Link>.
            </p>
            <div style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-tertiary)' }}>
              Already have an account?{' '}
              <button type="button" onClick={() => goTo('login')} style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', fontWeight: 700, color: 'var(--ember-strong)', cursor: 'pointer' }}>
                Log in
              </button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, paddingTop: 14, borderTop: '1px solid var(--border)', fontSize: 11, color: 'var(--text-tertiary)' }}>
              <LucideIcon name="status-locked" size={12} /> Secure authentication
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default UsernameBasedAuth;
